import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { InvoiceDataDeepLinkMode } from '@subscriptions/presentation/constants';
import {
  INVOICE_DATA_MODE_PARAM,
  INVOICE_DATA_UUID_PARAM,
  INVOICE_ERROR_MAX_LENGTH,
  INVOICE_ERROR_PARAM,
  InvoiceDataDeepLinkModes,
  ONLY_FAILED_PARAM,
  PAYMENT_UUID_PARAM,
} from '@subscriptions/presentation/constants';

/**
 * Reads and writes the fiscal data sheet's query params.
 *
 * Single owner of the `?invoiceDataUuid=` / `?invoiceDataMode=` / `?invoiceError=` /
 * `?paymentUuid=` params, so the inbound (url opens the sheet) and outbound (opening the
 * sheet updates the url) directions agree on the param names and on how the rest of the
 * query string is preserved.
 *
 * `paymentUuid` and `onlyFailed` are read-only here: they are written by the retry link
 * (buildInvoiceDataRetryPath) and only survive while the sheet stays in retry mode.
 *
 * Writes go through `history.replace` — consistent with the `?tab=` handling in
 * BlockDetailTabs, and it keeps the back button pointing at the previous page
 * rather than accumulating one entry per sheet open/close.
 *
 * The setters read `history.location` instead of closing over `search`, which keeps
 * them referentially stable across renders. That matters because they end up in the
 * dependency arrays of the callbacks that open the sheet.
 *
 * @returns {Object} Current param values and stable setters
 *
 * @example
 * const { clearTarget, setTarget, targetUuid } = useInvoiceDataSheetParams();
 */
export const useInvoiceDataSheetParams = () => {
  const history = useHistory();
  const { search } = useLocation();

  const params = new URLSearchParams(search);
  const targetUuid = params.get(INVOICE_DATA_UUID_PARAM);
  const mode = params.get(INVOICE_DATA_MODE_PARAM);
  // Truncated on read as well as on write: the param is user-editable, so the cap
  // cannot be enforced by the builder alone.
  const errorMessage = params.get(INVOICE_ERROR_PARAM)?.slice(0, INVOICE_ERROR_MAX_LENGTH) ?? null;
  // Only meaningful alongside the retry mode — it is the payment the retry sheet re-runs.
  const paymentUuid = params.get(PAYMENT_UUID_PARAM);
  // Absent means "re-issue the whole payment", which is the safe default for a hand-edited link.
  const onlyFailed = params.get(ONLY_FAILED_PARAM) === 'true';

  const replaceParams = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const { pathname, search: currentSearch } = history.location;
      const next = new URLSearchParams(currentSearch);

      mutate(next);

      const nextSearch = next.toString();
      if (nextSearch === new URLSearchParams(currentSearch).toString()) return;

      history.replace({ pathname, search: nextSearch ? `?${nextSearch}` : '' });
    },
    [history],
  );

  const setTarget = useCallback(
    (uuid: string, nextMode?: InvoiceDataDeepLinkMode, nextError?: string | null) => {
      replaceParams((next) => {
        next.set(INVOICE_DATA_UUID_PARAM, uuid);

        const trimmedError = nextError?.trim();
        // Resolved explicitly rather than left alone, so switching records cannot
        // inherit a stale mode or error from whichever sheet was open before.
        if (trimmedError) {
          next.set(INVOICE_ERROR_PARAM, trimmedError.slice(0, INVOICE_ERROR_MAX_LENGTH));
        } else {
          next.delete(INVOICE_ERROR_PARAM);
        }

        if (nextMode && nextMode !== InvoiceDataDeepLinkModes.EDIT) {
          next.set(INVOICE_DATA_MODE_PARAM, nextMode);
          return;
        }
        next.delete(INVOICE_DATA_MODE_PARAM);
        // The payment uuid and its scope belong to the retry flow only. They are left untouched
        // above so a retry link keeps what it arrived with, and dropped here so switching to
        // edit cannot carry them over.
        next.delete(PAYMENT_UUID_PARAM);
        next.delete(ONLY_FAILED_PARAM);
      });
    },
    [replaceParams],
  );

  const clearTarget = useCallback(() => {
    replaceParams((next) => {
      next.delete(INVOICE_DATA_UUID_PARAM);
      next.delete(INVOICE_DATA_MODE_PARAM);
      next.delete(INVOICE_ERROR_PARAM);
      next.delete(PAYMENT_UUID_PARAM);
      next.delete(ONLY_FAILED_PARAM);
    });
  }, [replaceParams]);

  return { clearTarget, errorMessage, mode, onlyFailed, paymentUuid, setTarget, targetUuid };
};
